import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [staffData, setStaffData] = useState(() => {
    // Load from localStorage on init
    const stored = localStorage.getItem('staffData');
    return stored ? JSON.parse(stored) : null;
  });

  const [authState, setAuthState] = useState(() => {
    // Load from localStorage on init for backward compatibility
    const stored = localStorage.getItem('staff_auth');
    return stored ? JSON.parse(stored) : {};
  });

  useEffect(() => {
    // Persist to localStorage
    localStorage.setItem('staff_auth', JSON.stringify(authState));
  }, [authState]);

  useEffect(() => {
    // Sync staff data with auth state
    if (staffData) {
      const role = staffData.role;
      setAuthState(prev => ({
        ...prev,
        [role]: true,
        pos: role === 'admin' || role === 'pos' || role === 'manager',
        kitchen: role === 'admin' || role === 'kitchen' || role === 'manager',
        admin: role === 'admin' || role === 'manager' // Manager can access admin dashboard
      }));
    }
  }, [staffData]);

  const loginWithStaff = (staff, token) => {
    localStorage.setItem('staffData', JSON.stringify(staff));
    localStorage.setItem('staffToken', token);
    setStaffData(staff);
    return true;
  };

  const logout = (role) => {
    // Clear staff data
    localStorage.removeItem('staffData');
    localStorage.removeItem('staffToken');
    setStaffData(null);
    
    // Clear auth state
    setAuthState({});
    localStorage.removeItem('staff_auth');
  };

  const isAuthenticated = (role) => {
    if (!staffData) return false;
    
    const staffRole = staffData.role;
    
    // Admin has access to everything
    if (staffRole === 'admin') return true;
    
    // Manager has access to POS, Kitchen, AND Admin (but location-restricted)
    if (staffRole === 'manager') {
      return role === 'pos' || role === 'kitchen' || role === 'admin';
    }
    
    // Specific role access
    return staffRole === role;
  };

  // Check if user has access to a specific outlet
  const hasOutletAccess = (outletId) => {
    if (!staffData) return false;
    
    // Admin has access to all outlets
    if (staffData.role === 'admin') return true;
    
    // Empty outlet_ids means no access (except admin)
    if (!staffData.outlet_ids || staffData.outlet_ids.length === 0) return false;
    
    // Check if outlet is in the allowed list
    return staffData.outlet_ids.includes(outletId);
  };

  // Get the list of outlets user can access
  const getAllowedOutlets = () => {
    if (!staffData) return [];
    
    // Admin has access to all outlets (return empty to indicate "all")
    if (staffData.role === 'admin') return [];
    
    return staffData.outlet_ids || [];
  };

  // Check if user is a manager (location-restricted admin)
  const isManager = () => {
    return staffData?.role === 'manager';
  };

  // Check if user is full admin
  const isAdmin = () => {
    return staffData?.role === 'admin';
  };

  const getStaffData = () => staffData;

  const value = {
    login: loginWithStaff, // Renamed for backward compatibility
    loginWithStaff,
    logout,
    isAuthenticated,
    hasOutletAccess,
    getAllowedOutlets,
    isManager,
    isAdmin,
    getStaffData,
    staffData
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
